import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import './IngestionApoHistory.scss';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import {PAGE_SIZE, ToastType} from '../../../shared/constants';
import {t} from 'i18next';
import {useNavigate} from 'react-router-dom';
import Box from '@mui/material/Box';
import ArrButton from '../../common/arr-button/ArrButton';
import {Typography} from '@mui/material';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {ReactComponent as LeftArrowOutlineIcon} from '../../../assets/images/left-arrow-outline.svg';
import {
  useApoList,
  useApoProgress,
  useApoRefresh,
} from '../../../hooks/ingestions/useIngestions';
import {
  IApoListRequest,
  IApoProgressApiResponse,
} from '../../../api/types/ingestionApi.type';
import {ReactComponent as CompletedState} from '../../../../src/assets/images/completed-state.svg';
import {ReactComponent as InProgressState} from '../../../../src/assets/images/inprogress-state.svg';
import {ReactComponent as ErrorState} from '../../../../src/assets/images/error-state.svg';
import ProgressLoader from './progress-loader/ProgressLoader';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';

const cleanPayload = (payload: IApoListRequest): IApoListRequest => {
  return Object.fromEntries(
    Object.entries(payload).filter(
      ([_, value]) => value !== null && value !== undefined
    )
  ) as IApoListRequest;
};

const ROW_HEIGHT = 36;

const getStateMeta = (value?: string) => {
  const s = String(value ?? '').toLowerCase();

  if (s.includes('completed')) {
    return {icon: <CompletedState />, label: 'Completed'};
  }
  if (s.includes('inprogress') || s.includes('in progress')) {
    return {icon: <InProgressState />, label: 'In Progress'};
  }
  if (s.includes('error')) {
    return {icon: <ErrorState />, label: 'Failure'};
  }
  return {icon: null, label: value || ''};
};

export const ApoColumns: GridColDef[] = [
  {
    field: 'user',
    headerName: t('ingestions.mapped.ingestionApo.user'),
    flex: 1,
  },
  {
    field: 'started',
    headerName: t('ingestions.mapped.ingestionApo.started'),
    flex: 1,
  },
  {
    field: 'completed',
    headerName: t('ingestions.mapped.ingestionApo.completed'),
    flex: 1,
  },
  {
    field: 'status',
    headerName: t('ingestions.mapped.ingestionApo.status'),
    flex: 1,
    renderCell: (params: GridRenderCellParams) => {
      const {icon, label} = getStateMeta(params?.value);
      return (
        <div className="apo-status-cell">
          {icon}
          <span>{label}</span>
        </div>
      );
    },
  },
  {
    field: 'custodians',
    headerName: t('ingestions.mapped.ingestionApo.custodians'),
    flex: 1,
  },
  {
    field: 'eFiles',
    headerName: t('ingestions.mapped.ingestionApo.eFiles'),
    flex: 1,
  },
  {
    field: 'emailAddress',
    headerName: t('ingestions.mapped.ingestionApo.emailAddress'),
    flex: 1,
  },
  {
    field: 'folders',
    headerName: t('ingestions.mapped.ingestionApo.folders'),
    flex: 1,
  },
  {
    field: 'mailStores',
    headerName: t('ingestions.mapped.ingestionApo.mailStores'),
    flex: 1,
  },
];

const IngestionApoHistory: React.FC = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isRefreshDisabled, setIsRefreshDisabled] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'Started', sort: 'asc'},
  ]);

  const navigate = useNavigate();

  const payload = {
    ...cleanPayload({
      sortingColumn: sortModel[0]?.field ?? 'Started',
      sortingOrder: sortModel[0]?.sort === 'asc' ? 1 : 0,
      pageNumber,
      pageSize,
    }),
  };

  const {data: apoList, isLoading, refetch} = useApoList(payload);
  const {data: apoProgressData, refetch: refetchProgress} = useApoProgress();
  const {mutateAsync: triggerRefreshAsync} = useApoRefresh();

  // Tracks whether polling was actively running so we can trigger a single grid refetch when it stops
  const wasPollingRef = useRef(false);

  const formatStartAndCompletedDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);

    if (isNaN(date.getTime())) return '';

    return date.toLocaleTimeString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      second: '2-digit',
    });
  };
  const apoGridWithTempId = useMemo(
    () =>
      apoList?.data.map((u, i) => ({
        ...u,
        tempId: i + 1,
        started: formatStartAndCompletedDate(u.started),
        completed: formatStartAndCompletedDate(u.completed),
      })) ?? [],
    [apoList?.data]
  );

  // Detect polling transition: in-progress → done (default values) → refetch grid once
  useEffect(() => {
    if (apoProgressData === undefined) return;

    const isInProgress =
      apoProgressData?.showProgress === true &&
      /inprogress/i.test(String(apoProgressData?.statusCode ?? ''));

    if (isInProgress) {
      wasPollingRef.current = true;
    } else if (wasPollingRef.current) {
      wasPollingRef.current = false;
      refetch();
    }
  }, [apoProgressData, refetch]);

  const progressInfo = useMemo(() => {
    const row: IApoProgressApiResponse | undefined = apoProgressData;
    return {
      show: true,
      status:
        row?.statusCode?.toLowerCase() === 'inprogress' ? 'Running' : 'Failure',
      progressPercent: row?.percentageCompleted ?? 0,
      updatedDocs: row?.updatedCount ?? 0,
      row,
    };
  }, [apoProgressData]);

  const onRefresh = useCallback(async () => {
    try {
      await triggerRefreshAsync();
      await refetch?.();
      await refetchProgress?.();
      setRefreshSuccess(true);
    } catch (err: any) {
      const detail: string = err?.response?.data?.detail ?? err?.message ?? '';
      const message = detail.includes('52001000025')
        ? t('ingestions.mapped.ingestionApo.somethingWentWrong')
        : (err?.message ??
          t('ingestions.mapped.ingestionApo.somethingWentWrong'));
      setRefreshError(message);
    }
  }, [isRefreshDisabled, triggerRefreshAsync, refetch]);

  return (
    <Box className="view-apo">
      {/* Header */}
      <Box className="apo-header">
        <Box className="apo-header-left" data-testid="apo-header-left">
          <Box className="apo-title" data-testid="apo-box-title">
            <Typography variant="subtitle1" data-testid="apo-grid-description">
              {t('ingestions.mapped.ingestionApo.description')}
            </Typography>
            <ArrTooltipWrapper
              trigger={
                <InfoIcon
                  className="info-icon"
                  data-testid="apo-tooltip-icon"
                />
              }
            >
              {t('ingestions.mapped.ingestionApo.tooltipText1')}
              <br />
              <br />
              {t('ingestions.mapped.ingestionApo.tooltipText2')}
            </ArrTooltipWrapper>
          </Box>

          <Box
            className="apo-back"
            onClick={() => navigate('/Ingestion/home')}
            data-testid="apo-back-button-wrapper"
          >
            <LeftArrowOutlineIcon className="apo-grid-icon-button" />
            <ArrButton variant="text" size="small" dataTestId="apo-back-button">
              {t('ingestions.mapped.ingestionApo.navigationLink')}
            </ArrButton>
          </Box>
        </Box>

        <ArrButton
          variant="primary"
          disabled={
            apoProgressData?.showProgress &&
            apoProgressData?.statusCode?.toLowerCase() === 'inprogress'
          }
          size="medium"
          dataTestId="apo-refresh-button"
          onClick={onRefresh}
        >
          {t('ingestions.mapped.ingestionApo.refreshApo')}
        </ArrButton>
      </Box>

      {/* Progress Loader */}
      {apoProgressData?.showProgress && (
        <Box
          className="apo-progress-wrapper"
          data-testid="apo-progress-wrapper"
        >
          <Box className="apo-progress-inner" data-testid="apo-progress-inner">
            <ProgressLoader {...progressInfo} />
          </Box>
        </Box>
      )}

      {/* Grid */}
      <div>
        <Box
          className="apo-history-header"
          data-testid="apo-history-box-header"
        >
          <Typography variant="h6" data-testid="apo-history-title">
            {t('ingestions.mapped.ingestionApo.history')}
          </Typography>
        </Box>
        <div data-testid="apo-history-table">
          <ArrDataGrid
            rowHeight={ROW_HEIGHT}
            rows={apoGridWithTempId}
            columns={ApoColumns}
            rowCount={apoList?.totalRecords}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            itemsPerPage={pageSize}
            setItemsPerPage={(v) => {
              setPageSize(v);
              setPageNumber(1);
            }}
            paginationMode="server"
            onPageChange={setPageNumber}
            primaryKey="tempId"
            onSortModelChange={setSortModel}
            sortModel={sortModel}
            className="view-apo-grid"
            loading={isLoading}
            pageSizeOptions={[10, 25, 50, 100]}
            noRowsOverlayCustomText={t(
              'ingestions.mapped.ingestionApo.noResults'
            )}
          />
        </div>
      </div>

      {refreshSuccess && (
        <ArrToastNotification
          message={t('ingestions.mapped.ingestionApo.refreshSuccess')}
          status={[ToastType.Success]}
          onClose={() => setRefreshSuccess(false)}
          data-testid="apo-refresh-success-toast"
        />
      )}

      {refreshError && (
        <ArrToastNotification
          message={refreshError}
          status={[ToastType.Error]}
          onClose={() => setRefreshError(null)}
          data-testid="apo-refresh-error-toast"
        />
      )}
    </Box>
  );
};

export default IngestionApoHistory;
